"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

// --- Types ---
export type ColorPalette = {
    id: string;
    label: string;
    primary: string;
    secondary: string;
    foreground: string;
};

export type FontOption = {
    id: string;
    label: string;
    class: string;
};

export type ThemeMode = "light" | "dark";

export type ThemeConfig = {
    palette: ColorPalette;
    font: FontOption;
    logoSize: number;
    systemName: string;
    themeMode: ThemeMode;
};

const THEME_STORAGE_KEY = "nowey-theme";

// --- Default Values ---
export const PALETTES: ColorPalette[] = [
    { id: "eco", label: "Eco Fresh", primary: "#49c937", secondary: "#91dbe1", foreground: "#ffffff" },
    { id: "ocean", label: "Ocean Blue", primary: "#2563eb", secondary: "#60a5fa", foreground: "#ffffff" },
    { id: "royal", label: "Royal Purple", primary: "#7c3aed", secondary: "#a78bfa", foreground: "#ffffff" },
    { id: "berry", label: "Pink Berry", primary: "#db2777", secondary: "#f472b6", foreground: "#ffffff" },
    { id: "fire", label: "Sunset Fire", primary: "#ea580c", secondary: "#fdba74", foreground: "#ffffff" },
    { id: "teal", label: "Clean Teal", primary: "#0d9488", secondary: "#5eead4", foreground: "#ffffff" },
    { id: "midnight", label: "Deep Navy", primary: "#1e3a8a", secondary: "#93c5fd", foreground: "#ffffff" },
    { id: "luxury", label: "Gold Luxury", primary: "#854d0e", secondary: "#facc15", foreground: "#ffffff" },
    { id: "cyber", label: "Cyber Punk", primary: "#111827", secondary: "#22d3ee", foreground: "#ffffff" },
    { id: "mono", label: "Minimalist", primary: "#000000", secondary: "#ffffff", foreground: "#ffffff" },
];

export const FONTS: FontOption[] = [
    { id: "font-sans", label: "Inter (Padrão)", class: "font-sans" },
    { id: "font-serif", label: "Serif Elegant", class: "font-serif" },
    { id: "font-mono", label: "Tech Mono", class: "font-mono" },
];

const DEFAULT_THEME: ThemeConfig = {
    palette: PALETTES[9],
    font: FONTS[0],
    logoSize: 64,
    systemName: "NoWey",
    themeMode: "dark",
};

// --- Context ---
type ThemeContextType = {
    theme: ThemeConfig;
    updateTheme: (updates: Partial<ThemeConfig>) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// --- Provider ---
export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const loadThemeFromStorage = () => {
            try {
                const raw = localStorage.getItem(THEME_STORAGE_KEY);
                if (!raw) return;

                const parsed = JSON.parse(raw) as Partial<ThemeConfig> | null;
                if (!parsed) return;

                const nextThemeMode: ThemeMode =
                    parsed.themeMode === "dark" || parsed.themeMode === "light" ? parsed.themeMode : DEFAULT_THEME.themeMode;

                const nextPalette =
                    typeof parsed.palette?.id === "string" ? PALETTES.find((p) => p.id === parsed.palette?.id) : undefined;
                const palette = nextPalette ?? DEFAULT_THEME.palette;

                const nextFont = typeof parsed.font?.id === "string" ? FONTS.find((f) => f.id === parsed.font?.id) : undefined;
                const font = nextFont ?? DEFAULT_THEME.font;

                const logoSize = typeof parsed.logoSize === "number" && Number.isFinite(parsed.logoSize) ? parsed.logoSize : DEFAULT_THEME.logoSize;
                const systemName = typeof parsed.systemName === "string" && parsed.systemName.trim().length > 0 ? parsed.systemName : DEFAULT_THEME.systemName;

                setTheme({
                    ...DEFAULT_THEME,
                    palette,
                    font,
                    logoSize,
                    systemName,
                    themeMode: nextThemeMode,
                });
            } catch {
                // ignore invalid storage payload
            }
        };

        loadThemeFromStorage();
        setMounted(true);
    }, []);

    // Save to localStorage + apply only data-theme (cores vêm do sistema em globals.css)
    useEffect(() => {
        if (mounted) {
            localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(theme));
            const root = document.documentElement;
            root.setAttribute("data-theme", theme.themeMode === "dark" ? "dark" : "light");
        }
    }, [theme, mounted]);

    const updateTheme = (updates: Partial<ThemeConfig>) => {
        setTheme((prev) => ({ ...prev, ...updates }));
    };

    return (
        <ThemeContext.Provider value={{ theme, updateTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

// --- Hook ---
export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }
    return context;
}
